import React, { useEffect, useState } from 'react';
import { trackLeadGeneration, trackFormEvent } from '../../utils/analytics';

const NewsletterSection: React.FC = () => {
  const [showCustomSuccess, setShowCustomSuccess] = useState(false);

  useEffect(() => {
    // Only load GHL form scripts in production or when explicitly enabled
    const isProduction = process.env.NODE_ENV === 'production';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    let script: HTMLScriptElement | null = null;
    
    if (!isLocalhost || isProduction) {
      script = document.createElement('script');
      script.src = 'https://link.msgsndr.com/js/form_embed.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const style = document.createElement('style');
    style.textContent = `
      iframe[data-form-id="ZSCUM3AlpcWt0vOEavEf"] { 
        transition: all 0.3s ease; 
        border-radius: 12px; 
        z-index: 1 !important; 
        position: relative !important;
      }
      .ghl-form-container { 
        position: relative; 
        overflow: hidden; 
        backdrop-filter: blur(10px); 
        z-index: 1 !important; 
      }
      .ghl-form-container::before { 
        content: ''; 
        position: absolute; 
        top: 0; 
        left: 0; 
        right: 0; 
        height: 2px; 
        background: linear-gradient(90deg, #f59d40, #ea5830, #f59d40); 
        background-size: 200% 100%; 
        animation: shimmer 3s infinite; 
        z-index: 1; 
      }
      @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      .ghl-form-overlay { 
        position: absolute; 
        inset: 0; 
        background: transparent; 
        z-index: 1; 
        pointer-events: none !important; 
      }
      .ghl-form-container:hover { transform: translateY(-2px); box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
      
      /* Force GoHighLevel iframes to stay in their lane */
      iframe[src*="leadconnectorhq.com"], 
      iframe[src*="msgsndr.com"] {
        z-index: 1 !important;
        position: relative !important;
      }
    `;
    document.head.appendChild(style);

    const checkForSubmission = () => {
      const iframe = document.getElementById('inline-ZSCUM3AlpcWt0vOEavEf') as HTMLIFrameElement;
      if (iframe) {
        iframe.addEventListener('load', () => {
          setTimeout(() => {
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              if (iframeDoc && iframeDoc.body.innerText.toLowerCase().includes('thank you')) {
                setShowCustomSuccess(true);
                // Track newsletter signup completion
                trackFormEvent('newsletter_signup', 'complete');
                trackLeadGeneration('newsletter_section', 'newsletter');
              }
            } catch {
              setTimeout(() => {
                setShowCustomSuccess(true);
                // Track newsletter signup completion (fallback)
                trackFormEvent('newsletter_signup', 'complete');
                trackLeadGeneration('newsletter_section', 'newsletter');
              }, 2000);
            }
          }, 1000);
        });
      }
    };

    setTimeout(checkForSubmission, 2000);
    return () => {
      if (script && document.body.contains(script)) document.body.removeChild(script);
      if (document.head.contains(style)) document.head.removeChild(style);
    };
  }, []);

  if (showCustomSuccess) {
    return (
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-xl overflow-hidden relative">
            <div className="px-6 py-12 sm:px-12 text-center relative z-10">
              <h2 className="text-4xl font-extrabold tracking-tight text-gray-800 mb-4">Welcome to our AI community!</h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">🎉 Thank you for subscribing! You'll receive your first batch of AI prompts and workflows within 24 hours.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 relative bg-gray-50">
      {/* Subtle background daisies */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <svg width="100%" height="100%" viewBox="0 0 1200 600" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.12 }}>
          {/* Top-left corner daisy */}
          <g className="nl-daisy slow" transform="translate(120,120) scale(1.05)">
            {Array.from({ length: 12 }).map((_, i) => (
              <ellipse key={`ntl-${i}`} cx="0" cy="-34" rx="11" ry="34" fill="#fff" stroke="#708c69" strokeWidth="1.6" transform={`rotate(${i * 30})`} />
            ))}
            <circle r="14" fill="#f4a258" stroke="#708c69" strokeWidth="1.4" />
          </g>
          {/* Bottom-right corner daisy */}
          <g className="nl-daisy" transform="translate(1080,460) scale(1.1)">
            {Array.from({ length: 12 }).map((_, i) => (
              <ellipse key={`nbr-${i}`} cx="0" cy="-36" rx="12" ry="36" fill="#fff" stroke="#708c69" strokeWidth="1.6" transform={`rotate(${i * 30})`} />
            ))}
            <circle r="15" fill="#f4a258" stroke="#708c69" strokeWidth="1.4" />
          </g>

          <style>{`
            @keyframes nlSway { 0%,100% { transform: translate(var(--x, 0), var(--y, 0)) rotate(0deg); } 50% { transform: translate(calc(var(--x, 0) + 2px), calc(var(--y, 0) + 1px)) rotate(1deg); } }
            .nl-daisy { transform-origin: center; animation: nlSway 14s ease-in-out infinite; }
            .nl-daisy.slow { animation-duration: 18s; }
          `}</style>
        </svg>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden relative ring-1 ring-[#708c69]/30">
          <div className="px-6 py-12 sm:px-12 lg:flex lg:items-center lg:py-16 relative z-10">
            <div className="lg:w-0 lg:flex-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#013d5a' }}>Stay updated with our newsletter</h2>
              <p className="text-base text-gray-700 leading-relaxed max-w-2xl">Get free AI prompts, optimization tips, and time-saving AI workflows to enhance your projects and daily tasks.</p>
            </div>
            <div className="mt-8 lg:mt-0 lg:ml-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#f4a258] to-[#708c69] rounded-2xl blur-sm opacity-30"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-[#708c69]/30 ghl-form-container transform hover:scale-[1.02] transition-all duration-300">
                  <div className="bg-white px-6 py-4 border-b border-[#708c69]/20">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-[#f4a258] rounded-full animate-pulse"></div>
                      <h4 className="text-lg font-semibold" style={{ color: '#013d5a' }}>Join Our AI Community</h4>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Get exclusive AI prompts & workflows</p>
                  </div>
                  <div className="p-6">
                    <div className="ghl-form-overlay"></div>
                    <iframe 
                      src="https://api.leadconnectorhq.com/widget/form/ZSCUM3AlpcWt0vOEavEf"
                      style={{ width: '100%', height: '240px', border: 'none', borderRadius: '12px', display: 'block' }}
                      id="inline-ZSCUM3AlpcWt0vOEavEf" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow"
                      data-activation-type="alwaysActivated" data-deactivation-type="neverDeactivate" data-form-name="Newsletter Signup"
                      data-height="240" data-layout-iframe-id="inline-ZSCUM3AlpcWt0vOEavEf" data-form-id="ZSCUM3AlpcWt0vOEavEf" title="Newsletter Signup"
                    />
                  </div>
                  <div className="bg-white px-6 py-3 border-t border-[#708c69]/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-600">Secure & Spam-free</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-600">Instant access</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-500">
                We care about your data. Read our{' '}
                <a href="/privacy" className="text-[#f4a258] font-medium hover:text-[#013d5a] underline transition-colors duration-300">Privacy Policy</a>
                {' '}and{' '}
                <a href="/terms" className="text-[#f4a258] font-medium hover:text-[#013d5a] underline transition-colors duration-300">Terms of Service</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection; 